import { isUnknownArray } from "./data";
/** Firestore REST protobuf JSON values, shared by the metered client/server boundary. */
export interface FirestoreValue {
  nullValue?: null | "NULL_VALUE";
  booleanValue?: boolean;
  integerValue?: string | number;
  doubleValue?: number | "NaN" | "Infinity" | "-Infinity";
  timestampValue?: string;
  stringValue?: string;
  bytesValue?: string;
  referenceValue?: string;
  geoPointValue?: { latitude: number; longitude: number };
  arrayValue?: { values?: FirestoreValue[] };
  mapValue?: { fields?: Record<string, FirestoreValue> };
}

export interface FirestoreDocument {
  name: string;
  fields?: Record<string, FirestoreValue>;
  createTime?: string;
  updateTime?: string;
}

export interface FieldTransform {
  fieldPath: string;
  setToServerValue?: "REQUEST_TIME";
  increment?: FirestoreValue;
  maximum?: FirestoreValue;
  minimum?: FirestoreValue;
  appendMissingElements?: { values?: FirestoreValue[] };
  removeAllFromArray?: { values?: FirestoreValue[] };
}

export interface FirestoreWrite {
  update?: FirestoreDocument;
  delete?: string;
  verify?: string;
  transform?: { document: string; fieldTransforms: FieldTransform[] };
  updateMask?: { fieldPaths: string[] };
  updateTransforms?: FieldTransform[];
  currentDocument?: { exists?: boolean; updateTime?: string };
}

export interface GatewayCursor { before: boolean; values: FirestoreValue[] }
export interface QueryOrder { field: { fieldPath: string }; direction: "ASCENDING" | "DESCENDING" }
export type FieldOperator = "EQUAL" | "NOT_EQUAL" | "LESS_THAN" | "LESS_THAN_OR_EQUAL"
  | "GREATER_THAN" | "GREATER_THAN_OR_EQUAL" | "ARRAY_CONTAINS" | "ARRAY_CONTAINS_ANY" | "IN" | "NOT_IN";
export type UnaryOperator = "IS_NULL" | "IS_NOT_NULL" | "IS_NAN" | "IS_NOT_NAN";
export type QueryFilter = { compositeFilter: { op: "AND" | "OR"; filters: QueryFilter[] } }
  | { fieldFilter: { field: { fieldPath: string }; op: FieldOperator; value?: FirestoreValue } }
  | { unaryFilter: { field: { fieldPath: string }; op: UnaryOperator } };
export interface StructuredQuery {
  from: { collectionId: string; allDescendants?: boolean }[];
  where?: QueryFilter;
  orderBy?: QueryOrder[];
  startAt?: GatewayCursor;
  endAt?: GatewayCursor;
  limit?: number;
}
export type GatewayRequest =
  | { operation: "get"; path: string; fresh?: boolean }
  | { operation: "query"; parent: string; structuredQuery: StructuredQuery; pageSize?: number; cursor?: GatewayCursor; fresh?: boolean }
  | { operation: "commit"; writes: FirestoreWrite[] };
export interface GatewayGetResponse { document: FirestoreDocument | null }
export interface GatewayQueryResponse { documents: FirestoreDocument[]; nextCursor?: GatewayCursor }
export interface GatewayCommitResponse { writeResults?: { updateTime?: string; transformResults?: FirestoreValue[] }[]; commitTime?: string }
export type GatewayResponse = GatewayGetResponse | GatewayQueryResponse | GatewayCommitResponse;

const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !isUnknownArray(value);

/** Wire data remains unknown until each recursively used protobuf field is checked. */
export function isFirestoreValue(value: unknown): value is FirestoreValue {
  if (!object(value)) return false;
  if (value.nullValue !== undefined && value.nullValue !== null && value.nullValue !== "NULL_VALUE") return false;
  if (value.booleanValue !== undefined && typeof value.booleanValue !== "boolean") return false;
  if (value.integerValue !== undefined && typeof value.integerValue !== "number" && typeof value.integerValue !== "string") return false;
  if (value.doubleValue !== undefined && typeof value.doubleValue !== "number"
    && value.doubleValue !== "NaN" && value.doubleValue !== "Infinity" && value.doubleValue !== "-Infinity") return false;
  for (const key of ["timestampValue", "stringValue", "bytesValue", "referenceValue"] as const) {
    if (value[key] !== undefined && typeof value[key] !== "string") return false;
  }
  if (value.geoPointValue !== undefined && (!object(value.geoPointValue)
    || typeof value.geoPointValue.latitude !== "number" || typeof value.geoPointValue.longitude !== "number")) return false;
  if (value.arrayValue !== undefined && (!object(value.arrayValue) || (value.arrayValue.values !== undefined
    && (!isUnknownArray(value.arrayValue.values) || !value.arrayValue.values.every(isFirestoreValue))))) return false;
  return value.mapValue === undefined || (object(value.mapValue) && (value.mapValue.fields === undefined
    || (object(value.mapValue.fields) && Object.values(value.mapValue.fields).every(isFirestoreValue))));
}

export function isFirestoreDocument(value: unknown): value is FirestoreDocument {
  return object(value) && typeof value.name === "string"
    && (value.createTime === undefined || typeof value.createTime === "string")
    && (value.updateTime === undefined || typeof value.updateTime === "string")
    && (value.fields === undefined || (object(value.fields) && Object.values(value.fields).every(isFirestoreValue)));
}
